// src/core/components/Modal/BulkCancelBookingModal/index.tsx
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { FormGroup, Row, Col, ControlLabel, Alert } from 'react-bootstrap'

import { Modal } from 'syft-acp-core/components/Modal'
import { getModal$ } from 'syft-acp-core/store/modals/selectors'
import { hideModal } from 'syft-acp-core/store/modals/actions'
import { cancelBookingsForWorker } from 'syft-acp-core/actions/bookings'
import { fetchCancellableBookings } from 'syft-acp-core/api/resources/bookings'
import DataDropdown from 'syft-acp-core/components/EditableTable/DataDropdown'
import EditableTextbox from 'syft-acp-core/components/EditableTextbox'
import FormattedDateTime from 'syft-acp-core/components/FormattedDateTime'
import { useTrackingTrigger } from '@indeed/flex-tracking-context'

import { endJobAssignmentReasons } from '../EndJobAssignmentModal/structure'
import { trackingEvents } from './BulkCancelBookingModal.tracking'
import { BulkCancelBookingModalProps, WorkerJobBooking } from './types'
import './index.css'

export const MODAL_NAME = 'bulkCancelBookingModal'
const OTHER_LABELS = ['Other - Involuntary', 'Other - Voluntary']
const WINDOW_24H_MS = 24 * 60 * 60 * 1000

const isOtherReason = (reason: string | number | null) =>
  typeof reason === 'string' && OTHER_LABELS.includes(reason)

const startMsOf = (b: WorkerJobBooking) =>
  typeof b.start_time === 'number' ? b.start_time : Date.parse(b.start_time)

// One selectable shift row: checkbox + date, a "late" badge when within 24h, and
// FC/CH badges ONLY when that shift's booking has them set (so flagged shifts stand out).
const ShiftRow = ({
  booking,
  checked,
  onToggle,
}: {
  booking: WorkerJobBooking
  checked: boolean
  onToggle: (shiftId: number) => void
}) => {
  const startMs = startMsOf(booking)
  const isLate = startMs - Date.now() < WINDOW_24H_MS

  return (
    <label className="shift-row">
      <span>
        <input type="checkbox" checked={checked} onChange={() => onToggle(booking.shift_id)} />{' '}
        {/* @ts-expect-error legacy component types */}
        <FormattedDateTime
          value={startMs}
          format={{ day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }}
        />
      </span>
      {isLate && <span className="late-badge">starts in &lt;24h · late</span>}
      {booking.syft_compensation && <span className="flag-badge fc">FC</span>}
      {booking.syft_unpaid_hours_compensation && <span className="flag-badge ch">CH</span>}
    </label>
  )
}

export const BulkCancelBookingModal = ({ dispatch, item }: BulkCancelBookingModalProps) => {
  const { worker = {}, jobID, currentShiftID, isBlockBooking = false } = item.options
  const isShown = item.isShown

  const [bookings, setBookings] = useState<WorkerJobBooking[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<number[]>([])
  const [reason, setReason] = useState<string | number | null>(null)
  const [otherReason, setOtherReason] = useState<string | undefined>(undefined)
  const triggerEvent = useTrackingTrigger()

  // Fetch the worker's currently-booked shifts for this job when the dialog opens.
  // The endpoint returns only active/upcoming/non-no-show bookings (cancelled excluded
  // server-side), so the list can't offer shifts the worker isn't booked on.
  useEffect(() => {
    if (!isShown || worker.id == null || jobID == null) {
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(undefined)
    fetchCancellableBookings({ workerID: worker.id, jobID })
      .then((res: { payload: WorkerJobBooking[] }) => {
        if (cancelled) return
        // handleCall resolves an envelope { payload, response, meta } — the bookings
        // array is on `payload` (see any direct api/resources consumer, e.g. DataEmployerVenue).
        const list = Array.isArray(res?.payload) ? res.payload : []
        setBookings(list)
        // Pre-tick the clicked shift if the worker is still booked on it.
        const match = list.find(b => Number(b.shift_id) === Number(currentShiftID))
        setSelected(match ? [match.shift_id] : [])
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoadError('Could not load this worker’s bookings. Please close and try again.')
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isShown, worker.id, jobID, currentShiftID])

  const resetState = useCallback(() => {
    setSelected([])
    setReason(null)
    setOtherReason(undefined)
    setBookings([])
    setLoadError(undefined)
  }, [])

  const closeModal = useCallback(() => {
    dispatch(hideModal(item.modalName))
    resetState()
  }, [dispatch, item.modalName, resetState])

  const toggleShift = (id: number) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]))

  const allIds = useMemo(() => bookings.map(b => b.shift_id), [bookings])
  const allSelected = selected.length === allIds.length && allIds.length > 0
  const toggleAll = () => setSelected(allSelected ? [] : allIds)

  const reasonLabel =
    typeof reason === 'number'
      ? endJobAssignmentReasons.find(r => r.id === reason)?.label
      : (reason as string | null)

  const otherValid = isOtherReason(reasonLabel ?? reason) ? !!otherReason : true
  const canSubmit = selected.length > 0 && !!reason && otherValid && !loading

  // Does the CURRENT selection include a shift with FC or CH set? Drives the
  // "last chance to configure flags" warning — shown only when it's actually relevant.
  const selectedHasFlag = useMemo(
    () =>
      bookings.some(
        b => selected.includes(b.shift_id) && (b.syft_compensation || b.syft_unpaid_hours_compensation),
      ),
    [bookings, selected],
  )

  const onConfirm = useCallback(() => {
    // Only attach the free-text custom reason when the selected reason is actually
    // an "Other" option — otherwise a stale value (typed, then switched away from
    // "Other") would leak into the payload.
    const customReason = isOtherReason(reasonLabel ?? reason) ? otherReason || null : null
    const payload = {
      worker_id: worker.id,
      job_id: jobID,
      shift_ids: [...selected],
      // shift_id scopes the post-cancel bookings refresh to the table's current shift.
      shift_id: currentShiftID,
      user_provided_reason: reasonLabel ?? reason,
      user_provided_custom_reason: customReason,
    }
    // Exclude the free-text custom reason from analytics — it can contain PII about the
    // worker. Track the structured fields only.
    const { user_provided_custom_reason: _omit, ...trackedPayload } = payload
    triggerEvent(trackingEvents.BULK_CANCEL_BOOKING.SAVE.CLICKED, { ...trackedPayload })
    dispatch(cancelBookingsForWorker(payload))
    closeModal()
  }, [
    selected,
    reason,
    reasonLabel,
    otherReason,
    worker,
    jobID,
    currentShiftID,
    closeModal,
    dispatch,
    triggerEvent,
  ])

  const count = selected.length

  return (
    <Modal
      className="bulk-cancel-modal"
      header={`Cancel ${worker.first_name || ''} ${worker.last_name || ''}`.trim()}
      isShown={isShown}
      isLoading={loading}
      canSubmit={canSubmit}
      onClose={closeModal}
      onConfirm={onConfirm}
      confirmKind="danger"
      confirmationText={`Cancel ${count} ${count === 1 ? 'shift' : 'shifts'}`}
      cancelText="Go back"
    >
      <FormGroup className="form-item">
        {loadError ? (
          <Alert bsStyle="danger">{loadError}</Alert>
        ) : loading ? (
          <div className="loading-row">Loading this worker’s bookings…</div>
        ) : (
          <>
            {isBlockBooking && (
              <Row>
                <Col md={12}>
                  <Alert bsStyle="info">
                    <strong>Block booking:</strong> only the shifts selected below will be cancelled. To
                    remove this worker from every upcoming shift shown, tick{' '}
                    <strong>Select all shifts</strong>.
                  </Alert>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={12}>
                <ControlLabel>
                  {worker.first_name} {worker.last_name} is booked on {bookings.length} upcoming{' '}
                  {bookings.length === 1 ? 'shift' : 'shifts'}. Select which to cancel:
                </ControlLabel>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <label style={{ fontWeight: 600 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Select all shifts
                </label>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                {bookings.map(b => (
                  <ShiftRow
                    key={b.shift_id}
                    booking={b}
                    checked={selected.includes(b.shift_id)}
                    onToggle={toggleShift}
                  />
                ))}
              </Col>
            </Row>

            {selectedHasFlag && (
              <Row>
                <Col md={12}>
                  <Alert bsStyle="warning">
                    <strong>Heads up:</strong> a selected shift has <strong>FC</strong> (Flex Compensation) or{' '}
                    <strong>CH</strong> (Compensation Hours) set. Cancelling removes the worker and this is
                    your last chance to configure those flags — this cannot be undone.
                  </Alert>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={12}>
                <DataDropdown
                  name="reasons"
                  onChange={(value: string) => setReason(value)}
                  value={reason}
                  editable
                  options={endJobAssignmentReasons}
                  showDisabled
                />
              </Col>
            </Row>
            {isOtherReason(reasonLabel ?? reason) && (
              <Row>
                <Col md={12}>
                  <EditableTextbox
                    value={otherReason}
                    placeholder="Please enter reason here"
                    hasControls={false}
                    onSave={(value?: string) => setOtherReason(value)}
                  />
                </Col>
              </Row>
            )}

            <Row style={{ marginBlockStart: 16 }}>
              <Col md={12}>
                <Alert bsStyle="danger">
                  <strong>Warning:</strong> cancelling happens <strong>immediately</strong> when you click,
                  and <strong>cannot be undone</strong>.
                </Alert>
              </Col>
            </Row>
          </>
        )}
      </FormGroup>
    </Modal>
  )
}

export default connect((state: any) => ({
  item: getModal$(state.modals)(MODAL_NAME),
  // @ts-expect-error getModal$ returns the generic ModalsItem; options is narrowed to
  // BulkCancelModalOptions once this modal is shown (see EndJobAssignmentModal).
}))(BulkCancelBookingModal)
